/* eslint-disable complexity */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { doRequest } from '@r1-frontend/do-request';
import { getConnectPrice, IGetConnectPriceResponseDto } from '@r1-frontend/api-domru/api-request/v1/get-connect-price';
import { condition, ICreditCondition } from '@r1-frontend/api-domru/payments/v1/credits/condition';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import OptionList, { TOption } from '@r1-frontend/ui-react/components/OptionList';
import CustomOptionList from '@r1-frontend/ui-react/components/OptionList/CustomOptionList';
import { FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { EVENTS } from '@r1-frontend/shared/constants/analytics';
import { EPriceTypes } from '@r1-frontend/shared/types/EPriceTypes';

import { fullBuyEquipAnalyticsBase } from '~/src/analytics';
import { USER_LOGIN_ROUTE } from '~/src/constants/routes';
import { getPriceOptions } from '~/src/entities/equipment';
import { convertShopPrices, EPriceOperationTypes } from '~/src/entities/price';
import LkPromotionalPopup from '~/src/features/specialOffersContainer/containers/LkPromotionalPopup';
import SpecOfferDetails from '~/src/features/specialOffersContainer/containers/SpecOfferDetails';
import { isNewDetailsDesign } from '~/src/features/specialOffersContainer/helpers';
import { setIsPopupOpened, setPopupState } from '~/src/features/specialOffersContainer/store/actions';
import { loadOffer, updateOffer } from '~/src/features/specialOffersContainer/store/actions/thunk';
import { selectOffer } from '~/src/features/specialOffersContainer/store/selectors';
import { IProps } from '~/src/features/specialOffersContainer/types';
import Credit from '~/src/features/work-in-home-ab/components/Description/components/Credit';
import { isCreditAbnEnabled } from '~/src/features/work-in-home-ab/components/Description/components/Credit/constants/abnVariants';
import Detail from '~/src/features/work-in-home-ab/components/Description/components/Detail';
import TestDrive from '~/src/features/work-in-home-ab/components/Description/components/TestDrive';
import { getTestDriveOptions } from '~/src/features/work-in-home-ab/components/Description/components/TestDrive/helpers';
import type { IDescription, PriceOptionProps } from '~/src/features/work-in-home-ab/types';
import FeedbackForm from '~/src/features/yandex-landing/FeedbackForm';
import { sortFunctionWithRequestIdOnTop } from '~/src/helpers/functions';
import { usePrepareQueryData } from '~/src/helpers/hooks/usePrepareQueryData';
import { useRouterChange } from '~/src/helpers/hooks/useRouterChange';
import type { TState } from '~/src/store';

import { setSelectedCreditCondition } from './components/Credit/context/actions';
import { useContextDispatch } from './components/Credit/context/hooks';
import { getSelectedCreditCondition } from './components/Credit/helpers';
import { checkAvailableDeliveryByPost, getPayOptions, getProductNeedsParams, ISelectedElement } from './functions';

import * as ST from './styled';

const INSTALLATION_VARIANT = 'installation';

const Description = ({ item, orderHandler, isEngineerDeliveryAvailable }: IDescription) => {
    const Router = useRouter();

    const dispatch = useDispatch();
    const contextDispatch = useContextDispatch();

    const {
        auth: { isAuth, clientProducts },
        city,
        isPopupOpened,
        popupOffer,
    } = useSelector((state: TState) => ({
        auth: state.auth,
        city: state.city.currentCity,
        isPopupOpened: state.specOffers.isPopupOpened,
        popupOffer: selectOffer(state),
    }), shallowEqual);

    const { services } = clientProducts || {};

    const [referrerPath, setReferrerPath] = useState('');
    const [selectedOption, setSelectedOption] = useState<PriceOptionProps | undefined>(undefined);
    const [isAvailablePost, setIsAvailablePost] = useState(false);
    const [creditConditions, setCreditConditions] = useState<ICreditCondition[]>([]);
    const [selectedPayOption, setSelectedPayOption] = useState<ISelectedElement>({
        optionId: 'sale',
        variantId: '',
    });

    const [isLoadingCallbackButton] = useRouterChange({
        only: [USER_LOGIN_ROUTE, '/full-buy-equip'],
    });

    const { withOption, duration: queryDuration, testDrive } = usePrepareQueryData();

    const priceOptions = useMemo<PriceOptionProps[]>(() => {
        const basePrices = getPriceOptions(
            convertShopPrices(item),
            null,
            false,
            (a, b) => sortFunctionWithRequestIdOnTop(a, b),
        );

        const fullPriceOption = basePrices.find(option => option.operType === EPriceOperationTypes.Possess);

        if (isCreditAbnEnabled() && item.isLoanAvailable && creditConditions && creditConditions.length > 0) {
            const creditOptions = creditConditions?.map(condition => ({
                id: `credit_${condition.duration}`,
                value: condition.duration,
                label: `Рассрочка на ${condition.duration} мес.`,
                price: condition.pay,
                type: EPriceTypes.Leasing,
                operType: EPriceOperationTypes.Leasing,
            }));

            return [...creditOptions, fullPriceOption];
        }

        return basePrices;
    }, [item, creditConditions]);

    const payOptions = useMemo<TOption[] | null>(() => getPayOptions(priceOptions), [priceOptions]);

    const testDriveOptions = useMemo(() => getTestDriveOptions(item.testDrive, priceOptions), [item.testDrive, priceOptions]);

    useEffect(() => {
        if (priceOptions) {
            const queryOption = Number(withOption);
            const priceOption = priceOptions.find(option => option.value === Number(queryDuration)) ??
                priceOptions[queryOption < priceOptions.length ? queryOption : 0];

            setSelectedOption(priceOption);

            if (Number(testDrive) && testDriveOptions) {
                setSelectedPayOption({
                    optionId: testDriveOptions.id,
                    variantId: priceOption.value,
                });
            } else if (payOptions) {
                setSelectedPayOption({
                    optionId: priceOption.operType,
                    variantId: priceOption.value,
                });
            }
        }
    }, [priceOptions, payOptions, testDriveOptions, testDrive, withOption, queryDuration]);

    useEffect(() => {
        if (window) {
            setReferrerPath(`?referrer=${location.pathname}&forceRedirect=true`);
        }
    }, []);

    useEffect(() => {
        (async() => {
            if (!isCreditAbnEnabled() || !item.prices.possess || item.prices.possess > 20000 || !item.isLoanAvailable) {
                return;
            }
            const response = await doRequest.payments<ICreditCondition[]>(
                condition(item.prices.possess),
            );
            if (response.isSuccess) {
                setCreditConditions(response.payload);
            }
        })();
    }, [item.prices.possess]);

    useEffect(() => {
        (async() => {
            const productNeedsParams = getProductNeedsParams(Number(item.restrictions[0]?.productId));

            const resp = await doRequest.apiRequest<IGetConnectPriceResponseDto>(
                getConnectPrice({
                    ...productNeedsParams,
                    int100: 0,
                    install: 2,
                }),
            );

            if (resp.isSuccess) {
                const { leftovers, showForDelivery, checkProductId } = item;

                const _isAvailablePost = checkAvailableDeliveryByPost({
                    services,
                    neededProductId: checkProductId || 0,
                    leftovers: leftovers || 0,
                    showForDelivery: !!showForDelivery,
                });
                setIsAvailablePost(_isAvailablePost);
            }
        })();
    }, [isAvailablePost, item, services]);

    const onRequestHandle = useCallback(() => {
        if (item && selectedOption) {
            const { requestId } = selectedOption;

            orderHandler(
                item,
                selectedOption,
                INSTALLATION_VARIANT,
                Number(selectedPayOption.optionId === EPriceOperationTypes.TestDrive),
                requestId,
            );
        }
    }, [orderHandler, item, selectedOption, selectedPayOption]);

    const setLabel = (variantId: string | number) => {
        return fullBuyEquipAnalyticsBase.buildDataLayerLabel({
            equipmentTitle: item.title,
            category: item.category?.parentName || item.category?.name || '', // the parentName is used as an actual category name when it's not empty (e.g. for yandex-stanciya)
            leasing: selectedOption?.value || 0,
            cost: selectedOption?.price,
            deliveryType: variantId === INSTALLATION_VARIANT ? 'serviceEngineerDelivery' : 'russianPost',
            testDrive: Number(testDrive),
        });
    };

    const onClickPayOption = (optionId: string | number, variantId: string | number) => {
        setSelectedPayOption({
            optionId: optionId,
            variantId: variantId,
        });

        const priceOption = priceOptions.find(el => Number(el.value) === Number(variantId));
        const selectedCreditCondition = getSelectedCreditCondition(creditConditions, variantId);
        if (selectedCreditCondition) {
            contextDispatch(setSelectedCreditCondition(selectedCreditCondition));
        }
        setSelectedOption(priceOption);
    };

    const onClickHandler = useCallback(() => {
        if (isAuth) {
            onRequestHandle();
        } else {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: 'authorization',
                action: 'clicked login btn on _user_login page',
            });
            Router.push(USER_LOGIN_ROUTE + referrerPath);
        }
    }, [isAuth, onRequestHandle, referrerPath, Router]);

    const onClickPromoLabel = useCallback(
        (id: string | number) => {
            const index = priceOptions.findIndex(el => Number(el.value) === Number(id));

            if (~index) {
                const { requestId } = priceOptions[index];
                requestId && dispatch(loadOffer(requestId));
            }
        },
        [priceOptions, dispatch],
    );

    const onCloseSpecOffer = useCallback(
        () => {
            batch(() => {
                dispatch(setPopupState('none'));
                dispatch(setIsPopupOpened(false));
            });
        },
        [dispatch],
    );

    const selectedTestDriveOption = useMemo(
        () => {
            if (!testDriveOptions) {
                return undefined;
            }

            if (selectedPayOption.optionId !== EPriceOperationTypes.TestDrive) {
                const [{ id }] = testDriveOptions.variants;
                return priceOptions.find(el => Number(el.value) === Number(id));
            }

            return selectedOption;
        },
        [testDriveOptions, selectedOption, selectedPayOption, priceOptions],
    );

    const onClickTestDriveOrder = useCallback(
        () => {
            if (!item || !selectedTestDriveOption) {
                return;
            }

            /*
            * @todo Передавать аргументы обьектом, чтобы не добавлять комментарии/лишние переменные для понимания
            * */
            orderHandler(
                item,
                selectedTestDriveOption,
                INSTALLATION_VARIANT,
                1, // testDrive
                selectedTestDriveOption.requestId,
            );
        },
        [item, orderHandler, selectedTestDriveOption],
    );

    const isNewOfferDetails = useMemo(
        () => popupOffer?.isShowNewDetail && isNewDetailsDesign(),
        [popupOffer?.isShowNewDetail],
    );

    const promoPopupProps: IProps<'onActivate'> = {
        isPopupOpen: isPopupOpened,
        offer: popupOffer,
        callbackMap: {
            onActivate: () => ({
                callback: async() => {
                    dispatch(updateOffer());
                },
            }),
            onEquipment: (offer) => ({
                on: () => offer.navigation?.type === 'equipment',
                loader: 'local',
                callback: async() => {
                    onRequestHandle();
                    onCloseSpecOffer();
                },
            }),
        },
        onClose: onCloseSpecOffer,
    };

    return (
        <>
            <FullWidthListContainer $direction={'column'} $wrap={'nowrap'} $size={32}>
                <Detail
                    data-test='description-detail'
                    city={city}
                    characteristics={item.props}
                    description={item.body}
                    marginBottom={(!isAuth || !isEngineerDeliveryAvailable) ? undefined : 4}
                />

                {creditConditions.length > 0 && (
                    <ST.BannerWrapper>
                        <Credit
                            creditConditions={creditConditions}
                            onChangeOption={onClickPayOption}
                            onOrder={onClickHandler}
                            dataLayerLabel={setLabel(INSTALLATION_VARIANT)}
                            selectedOptionId={selectedPayOption.optionId === 'credit' ? selectedPayOption.variantId : ''}
                        />
                    </ST.BannerWrapper>
                )}

                {testDriveOptions && (
                    <ST.BannerWrapper>
                        <TestDrive
                            equipment={item}
                            onChangeOption={onClickPayOption}
                            onOrder={onClickTestDriveOrder}
                            purchaseOption={selectedTestDriveOption}
                        />
                    </ST.BannerWrapper>
                )}

                <ST.Container>
                    {payOptions && (
                        <ST.WrappperComponent>
                            <ST.ComponentHeader data-test='description-pay-options-label'>
                                Варианты покупки
                            </ST.ComponentHeader>
                            <OptionList
                                data-test='description-pay-options-list'
                                options={payOptions}
                                selectedOptionId={selectedPayOption.optionId}
                                selectedVariantId={selectedPayOption.variantId}
                                onClickOption={onClickPayOption}
                                onClickOptionChip={onClickPromoLabel}
                                reverseVariants
                            />
                            {testDriveOptions && (
                                <CustomOptionList
                                    option={testDriveOptions}
                                    selectedOptionId={selectedPayOption.optionId}
                                    selectedVariantId={selectedPayOption.variantId}
                                    onClickOption={onClickPayOption}
                                    description={testDriveOptions.description}
                                    reverseVariants
                                />
                            )}
                        </ST.WrappperComponent>
                    )}

                    {(!isAuth || !isEngineerDeliveryAvailable && !isAvailablePost) && (
                        <FeedbackForm
                            data-test='description-feedback-form'
                            ref={null}
                            city={city}
                            title={'Оформление заявки на заказ'}
                            TitleComponent={ST.TitleComponent}
                            subtitle={`Оставьте заявку, и мы вам перезвоним, чтобы уточнить все детали.
                                    Или просто войдите в личный кабинет — так вы сможете заказать оборудование самостоятельно.`}
                            FormWrapper={ST.FormWrapper}
                            InputWrapperComponent={ST.InputWrapper}
                            InputNameComponent={ST.InputName}
                            InputPhoneComponent={ST.InputPhone}
                            ButtonWrapperComponent={ST.ButtonWrapper}
                            ButtonComponent={ST.SubmitButton}
                            buttonTitle={'Отправить заявку'}
                            namePlaceholder={'Как вас зовут?'}
                            buttonType={btnTypes.PRIMARY}
                            agreeAboveButton={true}
                            options={{ name: setLabel(INSTALLATION_VARIANT) }}
                        />
                    )}

                    {((isAuth && (isEngineerDeliveryAvailable || isAvailablePost)) || !isAuth) && (
                        <ST.CallbackButton
                            data-test='description-callback-button'
                            type={isAuth ? btnTypes.ACCENT : btnTypes.SECONDARY}
                            text={isAuth ? 'Оформить заказ' : 'Войти в личный кабинет'}
                            onClick={onClickHandler}
                            withTopSpace={!isAuth || !isEngineerDeliveryAvailable}
                            isLoading={isLoadingCallbackButton}
                        />
                    )}
                </ST.Container>
            </FullWidthListContainer>

            {isNewOfferDetails
                ? <SpecOfferDetails {...promoPopupProps}/>
                : <LkPromotionalPopup {...promoPopupProps}/>
            }
        </>
    );
};

export default Description;
